import PropTypes from 'prop-types';
import React, { useState } from 'react';
import injectSheet from 'react-jss';
import { Link } from 'react-router-dom-v5-compat';

import { changePassword } from '~/api/login';
import {
  black2,
  evisortRed,
  evisortYellow,
} from '~/assets/shared-styles/general';
import LoginPageContainer from '~/components/LoginPage/LoginPageContainer/LoginPageContainer';
import LoginPassword from '~/components/LoginPage/LoginPassword/LoginPassword';
import { getDecodedUserInfoFromLoginToken } from '~/components/LoginPage/utils';
import { trackSegment } from '~/components/SegmentAnalytics';
import LoadingSpinner from '~/components/Shared/Icons/LoadingSpinner';
import { LoginViewType } from '~/enums/LoginViewType';
import { QueryParamType } from '~/enums/QueryParamType';
import { useAsync } from '~/hooks/useAsync';
import { usePasswordsWithValidation } from '~/hooks/usePasswordsWithValidation';
import { FormattedMessage } from 'react-intl';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { getPageSearchQueryByKey } from '~/utils/searchQuery';

function ErrorMessageView({ classes }) {
  return (
    <div className={classes.errorMessage}>
      <FormattedMessage
        id="ChangePassword.errorMessage"
        defaultMessage="An error occurred. Please <link>go here</link> and try again."
        description="Error message with link to home page"
        values={{
          link: (chunks) => <Link to="/">{chunks}</Link>,
        }}
      />
    </div>
  );
}

function ChangedPasswordView() {
  return (
    <div>
      <FormattedMessage
        id="ChangePassword.successMessage"
        defaultMessage="Your password has been successfully updated. You will be redirected to the platform. If not, go to <link>login page</link> and log in."
        description="Success message with link to login page"
        values={{
          link: (chunks) => <Link to="/">{chunks}</Link>,
        }}
      />
    </div>
  );
}

function NoTokenView({ classes }) {
  return (
    <div className={classes.errorMessage}>
      <FormattedMessage
        id="ChangePassword.noTokenMessage"
        defaultMessage="No valid reset password link found."
        description="Error message when no valid token is found"
      />
    </div>
  );
}

function FormView({ classes, submitResetPassword, ...rest }) {
  const { validationResult } = rest;
  return (
    <div className={classes.formContainer}>
      <form onSubmit={submitResetPassword}>
        <LoginPassword {...rest} />
        <div className={classes.formButtonContainer}>
          <button
            type="submit"
            className={classes.formButton}
            onClick={submitResetPassword}
            disabled={!validationResult.allValid}
          >
            <FormattedMessage
              id="ChangePassword.saveButton"
              defaultMessage="Save"
              description="Save button text"
            />
          </button>
        </div>
      </form>
    </div>
  );
}

function Page(props) {
  const formatDate = useFormatDate();
  const { classes } = props;

  const [
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    validationResult,
  ] = usePasswordsWithValidation();
  const [view, setView] = useState();

  const token = getPageSearchQueryByKey(QueryParamType.Token);
  const slug = getPageSearchQueryByKey(QueryParamType.Slug);
  const tokenType = getPageSearchQueryByKey(QueryParamType.StytchTokenType);

  const formData = {
    new_password: password,
  };

  function submitResetPassword(e) {
    e.preventDefault();
    if (!token) setView(LoginViewType.TokenMissing);
    else {
      setView(LoginViewType.FormSubmitting);
      handleResetPassword();
    }
  }

  const { executor: handleResetPassword } = useAsync(
    changePassword,
    { token, slug, tokenType, formData },
    {
      successHandler: async (response) => {
        setView(LoginViewType.FormSubmitted);
        if (response?.msg) {
          const redirectUrl = response.msg;
          try {
            if (response.user_id || response.client_id) {
              trackSegment('User Reset Password', {
                groupId: response.client_id,
                userId: `${response.user_id}_${response.client_id}`,
                firstLoginDate: formatDate(new Date(), 'utc'),
              });
            } else {
              const { client_id, user_id } =
                getDecodedUserInfoFromLoginToken(redirectUrl);
              trackSegment('User Reset Password', {
                groupId: client_id,
                userId: `${user_id}_${client_id}`,
                firstLoginDate: formatDate(new Date(), 'utc'),
              });
            }
          } catch (error) {
            console.error(error);
          }
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 1000);
        }
      },
      errorHandler: () => {
        setView(LoginViewType.Error);
      },
    },
  );

  function renderView() {
    switch (view) {
      case LoginViewType.Error:
        return <ErrorMessageView classes={classes} />;
      case LoginViewType.FormSubmitting:
        return <LoadingSpinner />;
      case LoginViewType.FormSubmitted:
        return <ChangedPasswordView />;
      case LoginViewType.TokenMissing:
        return <NoTokenView />;
      default:
        return (
          <FormView
            classes={classes}
            submitResetPassword={submitResetPassword}
            password={password}
            setPassword={setPassword}
            confirmPassword={confirmPassword}
            setConfirmPassword={setConfirmPassword}
            validationResult={validationResult}
          />
        );
    }
  }

  return (
    <LoginPageContainer
      title={
        <FormattedMessage
          id="ChangePassword.pageTitle"
          defaultMessage="Create new password"
          description="Page title for change password form"
        />
      }
    >
      {renderView()}
    </LoginPageContainer>
  );
}

Page.propTypes = {
  classes: PropTypes.object.isRequired,
};

const styles = {
  formContainer: {
    margin: '40px auto',
    width: '360px',
  },
  formButtonContainer: {
    marginTop: '40px',
  },
  formButton: {
    padding: '10px 20px',
    width: '360px',
    display: 'inline-block',
    backgroundColor: evisortYellow,
    borderRadius: '3px',
    border: 'solid 1px rgba(0, 0, 0, 0.25)',
    color: black2,
    fontSize: '15px',
    cursor: 'pointer',
    textDecoration: 'none',
    '&:disabled': {
      opacity: 0.5,
    },
  },
  errorMessage: {
    margin: '20px',
    color: evisortRed,
    fontSize: '15px',
  },
};

export default injectSheet(styles)(Page);
